import React from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowLeft, BookOpen, Shield } from 'lucide-react';
import { MobileNav } from '../components/MobileNav';

const playfairFont = "'Playfair Display', serif";

const SECTIONS = [
  {
    title: '1. Definisi Kuji',
    items: [
      'Kuji adalah fitur hiburan/promosi berbasis slot hadiah.',
      'Setiap draw mendapatkan hadiah.',
      'Tidak ada "zonk" / "try again" — semua slot berisi hadiah.',
    ],
  },
  {
    title: '2. Transparansi Slot',
    items: [
      'Total slot ditampilkan secara publik untuk setiap event.',
      'Sisa slot dapat dilihat realtime di halaman event.',
      'Tier hadiah (Last One, Gold, Silver, Bronze, Common) ditampilkan beserta jumlah masing-masing.',
      'Pemenang ditampilkan secara realtime di Live Winner Feed dengan username disamarkan untuk privasi.',
    ],
  },
  {
    title: '3. Probabilitas Hadiah',
    items: [
      'Setiap hadiah memiliki jumlah dan peluang tertentu.',
      'Peluang dapat berubah sesuai sisa slot yang ada.',
      'Sistem menggunakan slot pool yang sudah dibuat sebelum event aktif — tidak dapat diubah setelah event berjalan.',
    ],
  },
  {
    title: '4. Last Prize',
    items: [
      'Last Prize diberikan pada slot terakhir event.',
      'Status Last Prize ditampilkan secara terbuka untuk semua user.',
      'Setelah event habis (semua slot di-draw), event otomatis ditutup.',
    ],
  },
  {
    title: '5. Coin & Pembayaran',
    items: [
      '1 Coin = Rp1.000.',
      'Coin yang sudah digunakan untuk draw tidak dapat dikembalikan kecuali terjadi error sistem.',
      'Pembayaran harus sesuai metode resmi LimHappyShop (QRIS / Transfer Bank).',
    ],
  },
  {
    title: '6. Hadiah & Inventori',
    items: [
      'Hadiah fisik masuk ke inventori user dan dapat diakses di menu Inventory.',
      'User dapat memilih Kirim Barang atau Jual Kembali jika tersedia.',
      'Hadiah digital/coin langsung masuk ke wallet user.',
    ],
  },
  {
    title: '7. Pengiriman Hadiah',
    items: [
      'Ongkos kirim ditanggung pemenang.',
      'Ongkir dihitung dari alamat pengiriman toko ke alamat customer berdasarkan rate kurir resmi (JNE).',
      'Customer wajib mengisi alamat lengkap (Provinsi, Kota, Kecamatan, Kode Pos).',
      'Admin akan memproses pengiriman setelah ongkir dibayar (QRIS atau Coin).',
    ],
  },
  {
    title: '8. Jual Kembali (Buyback)',
    items: [
      'Hadiah tertentu bisa dijual kembali menjadi Coin.',
      'Nilai jual kembali ditentukan admin saat event dibuat.',
      'Setelah dijual kembali, hadiah tidak bisa dikirim atau dikembalikan ke inventori.',
      'Aksi jual kembali tidak dapat dibatalkan.',
    ],
  },
  {
    title: '9. Batasan & Penyalahgunaan',
    items: [
      'Multi akun diperbolehkan jika tidak digunakan untuk kecurangan atau spam.',
      'Aktivitas bot, exploit, manipulasi sistem, atau transaksi mencurigakan dapat ditinjau admin.',
      'Admin berhak menahan proses atau membatalkan transaksi jika ditemukan indikasi penyalahgunaan.',
    ],
  },
  {
    title: '10. Error Sistem',
    items: [
      'Jika terjadi error teknis, admin dapat melakukan koreksi saldo, refund coin, atau perbaikan data.',
      'Semua koreksi dicatat dalam audit log internal untuk akuntabilitas.',
    ],
  },
  {
    title: '11. Perubahan Ketentuan',
    items: [
      'LimHappyShop dapat memperbarui ketentuan sewaktu-waktu.',
      'Perubahan akan berlaku setelah dipublikasikan di halaman ini.',
    ],
  },
  {
    title: '12. Kontak Bantuan',
    items: [
      'Hubungi customer support melalui menu Bantuan / Profile → Contact Support.',
      'WhatsApp: tersedia di footer aplikasi (jika dikonfigurasi admin).',
    ],
  },
];

export const KujiTerms = () => {
  const navigate = useNavigate();
  return (
    <div className="min-h-screen pb-24" style={{ background: '#080C0A', fontFamily: "'Outfit', sans-serif" }}>
      <header className="sticky top-0 z-40 backdrop-blur-2xl" style={{ background: 'rgba(8,12,10,0.9)', borderBottom: '1px solid rgba(255,255,255,0.1)' }}>
        <div className="max-w-3xl mx-auto px-6 py-4 flex items-center gap-4">
          <button onClick={() => navigate(-1)} className="hover:opacity-80" data-testid="kuji-terms-back-btn">
            <ArrowLeft className="w-6 h-6" style={{ color: '#F8FAF9' }} />
          </button>
          <h1 className="text-2xl font-light flex items-center gap-2" style={{ fontFamily: playfairFont, color: '#F8FAF9' }}>
            <BookOpen className="w-5 h-5" style={{ color: '#D4AF37' }} /> Syarat & Ketentuan Kuji
          </h1>
        </div>
      </header>

      <div className="max-w-3xl mx-auto px-6 py-8" data-testid="kuji-terms-content">
        <div className="rounded-2xl p-5 mb-6" style={{ background: 'rgba(212,175,55,0.06)', border: '1px solid rgba(212,175,55,0.3)' }}>
          <p className="text-sm flex items-center gap-2 mb-2" style={{ color: '#D4AF37' }}>
            <Shield className="w-4 h-4" /> Penting untuk dibaca sebelum melakukan draw
          </p>
          <p className="text-sm" style={{ color: '#CFE0D5' }}>
            Dokumen ini mengatur cara kerja sistem Kuji di LimHappyShop. Dengan melakukan draw, Anda menyatakan setuju dengan ketentuan di bawah ini.
          </p>
        </div>

        <div className="space-y-5">
          {SECTIONS.map((sec, idx) => (
            <section key={idx} className="rounded-2xl p-5" style={{ background: 'rgba(255,255,255,0.03)', border: '1px solid rgba(255,255,255,0.08)' }} data-testid={`kuji-terms-section-${idx}`}>
              <h2 className="text-lg font-light mb-2" style={{ fontFamily: playfairFont, color: '#D4AF37' }}>{sec.title}</h2>
              <ul className="space-y-1.5">
                {sec.items.map((it, i) => (
                  <li key={i} className="flex items-start gap-2 text-sm" style={{ color: '#CFE0D5' }}>
                    <span className="mt-1.5 w-1 h-1 rounded-full flex-shrink-0" style={{ background: '#D4AF37' }} />
                    <span>{it}</span>
                  </li>
                ))}
              </ul>
            </section>
          ))}
        </div>

        <p className="text-xs mt-8 text-center" style={{ color: '#6B8079' }}>
          Update terakhir: Februari 2026 · © LimHappyShop
        </p>
      </div>

      <MobileNav />
    </div>
  );
};
